import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { addDays, parseISO } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { publicApi } from "@/lib/api";
import { DatePill } from "./DatePill";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface PriceCalendarEntry {
  date: string;
  lowestPrice: number | null;
  tripCount: number;
}

interface DatePriceCarouselProps {
  originCityId: number;
  destinationCityId: number;
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  days?: number;
}

/**
 * DatePriceCarousel - horizontal scrollable date picker with prices
 *
 * Fetches price data from /api/public/:slug/trips/price-calendar and displays
 * DatePill components for each date. Auto-scrolls to selected date on load.
 */
export function DatePriceCarousel({
  originCityId,
  destinationCityId,
  selectedDate,
  onDateSelect,
  days = 14,
}: DatePriceCarouselProps) {
  const { slug } = useTenant();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const selectedPillRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Calculate start date (3 days before selected date, or today if that's earlier)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const idealStartDate = addDays(selectedDate, -3);
  const startDate = idealStartDate < today ? today : idealStartDate;
  const startDateStr = startDate.toISOString().split("T")[0];

  // Fetch price calendar data
  const { data: priceData, isLoading } = useQuery({
    queryKey: ["priceCalendar", slug, originCityId, destinationCityId, startDateStr, days],
    queryFn: () =>
      publicApi.get<PriceCalendarEntry[]>(
        `/api/public/${slug}/trips/price-calendar?originCityId=${originCityId}&destinationCityId=${destinationCityId}&startDate=${startDateStr}&days=${days}`
      ),
    enabled: !!slug && !!originCityId && !!destinationCityId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Auto-scroll to selected date on initial load
  useEffect(() => {
    if (selectedPillRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const pill = selectedPillRef.current;
      const containerWidth = container.clientWidth;
      const pillLeft = pill.offsetLeft;
      const pillWidth = pill.clientWidth;

      // Center the selected pill in the container
      const scrollPosition = pillLeft - (containerWidth / 2) + (pillWidth / 2);
      container.scrollTo({ left: scrollPosition, behavior: "smooth" });
    }
  }, [priceData]); // Run when data loads

  // Update arrow visibility on scroll
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      handleScroll(); // Initial check
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [priceData]);

  // Scroll by button click
  const scrollBy = (direction: "left" | "right") => {
    if (!scrollContainerRef.current) return;
    const scrollAmount = 300;
    scrollContainerRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="flex gap-3 overflow-hidden py-2">
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton key={i} className="min-w-[90px] h-[100px] rounded-lg" />
        ))}
      </div>
    );
  }

  if (!priceData || priceData.length === 0) {
    return null;
  }

  return (
    <div className="relative">
      {/* Left scroll arrow - desktop only */}
      <button
        onClick={() => scrollBy("left")}
        className={cn(
          "absolute left-0 top-1/2 -translate-y-1/2 z-10 hidden md:flex",
          "h-9 w-9 items-center justify-center bg-white border hairline-strong shadow-sheet",
          "hover:border-ink text-ink-muted hover:text-ink transition-[opacity,colors]",
          showLeftArrow ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        aria-label="Scroll left"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      {/* Scrollable container */}
      <div
        ref={scrollContainerRef}
        className={cn(
          "flex gap-3 overflow-x-auto py-2 px-1",
          "scroll-smooth snap-x snap-mandatory",
          "scrollbar-hide", // Hide scrollbar on mobile
          "-mx-4 px-4 md:mx-0 md:px-1", // Full bleed on mobile
          "touch-pan-x" // Optimize for horizontal touch scrolling
        )}
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch" // Momentum scrolling on iOS
        }}
      >
        {priceData.map((entry) => {
          const date = parseISO(entry.date);
          const isSelected =
            date.toISOString().split("T")[0] ===
            selectedDate.toISOString().split("T")[0];

          return (
            <div
              key={entry.date}
              ref={isSelected ? selectedPillRef : undefined}
              className="snap-center shrink-0"
            >
              <DatePill
                date={date}
                lowestPrice={entry.lowestPrice}
                tripCount={entry.tripCount}
                isSelected={isSelected}
                onClick={() => onDateSelect(date)}
              />
            </div>
          );
        })}
      </div>

      {/* Right scroll arrow - desktop only */}
      <button
        onClick={() => scrollBy("right")}
        className={cn(
          "absolute right-0 top-1/2 -translate-y-1/2 z-10 hidden md:flex",
          "h-9 w-9 items-center justify-center bg-white border hairline-strong shadow-sheet",
          "hover:border-ink text-ink-muted hover:text-ink transition-[opacity,colors]",
          showRightArrow ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        aria-label="Scroll right"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}
